import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import useDashboardNav from "../../../Hooks/u1/useDashboardNav";
import { DashboardSidebar } from "../../../components/admin/u1/DashboardSidebar";
import { DashboardTopbar } from "../../../components/admin/u1/DashboardTopbar";
import { MobileTabBar } from "../../../components/admin/u1/MobileTabBar";
import { useGetUserQuery } from "../../../store/auth/usersApiSlice";
import { setUserInfo, clearUserInfo } from "../../../store/auth/authSlice";
import { LoadingIcon, Modal, SessionExpired, Logout } from "../../../components/common";
import "../../../components/common/u1/tokens.css";

import { NAV_ICONS } from "../../../components/admin/u1/navIcons";

/* u1 dashboard shell — the rail and top bar every dashboard screen sits in. */
export default function RootLayoutAdminU1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);
  /* `refetchOnMountOrArgChange` is the real option name — `refetchOnMount`
     is not one RTK Query knows, so it was silently ignored and arriving at
     the dashboard served whatever copy of the viewer's record was already
     cached. That record carries the Help & Support ticket list, so a dispute
     raised a moment earlier (on the public layout, not this one) was missing
     from it until the page was reloaded by hand. */
  const { data, isLoading, isSuccess, isError, error } = useGetUserQuery(token, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });

  const nav = useDashboardNav({ icons: NAV_ICONS });
  const [isSessionExpired, setSessionExpired] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  useEffect(() => {
    if (!userInfo) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionExpiredPopup = () => {
    setSessionExpired(!isSessionExpired);
    dispatch(clearUserInfo());
    navigate("/login");
  };

  useEffect(() => {
    if (isError && error?.data?.code === "token_not_valid") setSessionExpired(true);
    if (isSuccess) dispatch(setUserInfo({ ...data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isError]);

  if (isLoading) {
    return (
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="u1-root">
      {(isSessionExpired || isError) && (
        <Modal modalHandle={sessionExpiredPopup}>
          <SessionExpired handleNavigate={sessionExpiredPopup} />
        </Modal>
      )}

      {logoutOpen && (
        <Modal modalHandle={() => setLogoutOpen(false)}>
          <Logout close={() => setLogoutOpen(false)} />
        </Modal>
      )}

      {isSuccess && (
        <div className="flex h-screen overflow-hidden bg-[var(--bg)]">
          {/* The rail is inline from sm (576px here) — below 1050px it is
              already collapsed to icons, which fits a tablet. Only phones get
              the drawer. */}
          <div className="hidden sm:block">
            <DashboardSidebar
              links={nav.links}
              collapsed={nav.collapsed}
              onToggleCollapse={nav.toggleCollapsed}
              onLogout={() => setLogoutOpen(true)}
              isEnterprise={nav.isEnterprise}
            />
          </div>

          {/* Mobile drawer. The scrim closes it, and so does following a link —
              otherwise the drawer would sit over the page just navigated to. */}
          {nav.mobileOpen && (
            <div className="fixed inset-0 z-50 sm:hidden">
              <button
                type="button"
                aria-label="Close navigation"
                onClick={() => nav.setMobileOpen(false)}
                className="absolute inset-0 bg-black/40"
              />
              <div className="relative h-full w-[240px]">
                <DashboardSidebar
                  links={nav.links}
                  onLogout={() => {
                    nav.setMobileOpen(false);
                    setLogoutOpen(true);
                  }}
                  isEnterprise={nav.isEnterprise}
                  onNavigate={() => nav.setMobileOpen(false)}
                  showCollapseControl={false}
                />
              </div>
            </div>
          )}

          <div className="flex w-full flex-col overflow-auto">
            <DashboardTopbar
              links={nav.links}
              unreadCount={nav.unreadCount}
              onOpenNav={() => nav.setMobileOpen(true)}
            />
            {/* The bar is fixed, so the page needs its height back as
                padding or the last rows sit under it. */}
            <div className="px-4 pb-6 sm:px-6 lg:px-8 max-[743px]:pb-[calc(58px+env(safe-area-inset-bottom,0px)+20px)]">
              <Outlet />
            </div>
          </div>

          <MobileTabBar links={nav.links} onOpenNav={() => nav.setMobileOpen(true)} />
        </div>
      )}
    </div>
  );
}
